import React, { useEffect, useState } from "react";
import './horizonTable.css';
import { toast } from "react-toastify";
import {
  adminEditModalOverlayStyle,
  adminEditModalHeaderStyle,
  adminEditModalTitleStyle,
  adminEditModalBodyStyle,
  adminEditModalFooterStyle,
  adminEditCancelBtnStyle,
  adminEditSaveBtnStyle,
  adminEditLabelStyle,
  adminEditInputStyle,
  ADMIN_THEME,
} from "./adminEditModalTheme";

const appUserModalContentStyle = {
  maxWidth: "1200px",
  margin: "0 auto",
  border: "none",
  borderRadius: 14,
  overflow: "hidden",
  boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
};

const appUserFieldStyle = {
  ...adminEditInputStyle,
  backgroundColor: "#fafafa",
  color: "#1f2937",
  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
};

const appUserInputIconStyle = {
  backgroundColor: "#fdf2f4",
  border: "1px solid #e2e8f0",
  borderRight: "none",
  color: ADMIN_THEME,
  minWidth: 42,
  justifyContent: "center",
};

const appUserPasswordToggleStyle = {
  backgroundColor: "#fdf2f4",
  border: "1px solid #e2e8f0",
  borderLeft: "none",
  color: ADMIN_THEME,
  cursor: "pointer",
};

const appUserPasswordSectionStyle = {
  borderTop: "1px solid #f0f0f0",
  paddingTop: 14,
  marginTop: 6,
};

function AppUsersPage() {
  const [editPasswordMode, setEditPasswordMode] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [showAddConfirmPassword, setShowAddConfirmPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: ''
  });
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageSize = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");

      if (!token) {
        setError("Authentication required. Please login again.");
        setLoading(false);
        return;
      }

      const res = await fetch(baseUrl + "/api/app-users", {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!res.ok) {
        if (res.status === 401) {
          setError("Authentication failed. Please login again.");
        } else if (res.status === 403) {
          setError("You don't have permission to access this data.");
        } else {
          setError(`Failed to load users. Server responded with ${res.status}.`);
        }
        setUsers([]);
        setLoading(false);
        return;
      }

      const data = await res.json();

      if (data.success && Array.isArray(data.data)) {
        setUsers(data.data);
        setError(null);
      } else {
        setUsers([]);
        setError(data.message || "Failed to load users data.");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setUsers([]);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Add User
  const handleAdd = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password || !newUser.confirmPassword) {
      toast.error("All fields are required");
      return;
    }
    if (!isValidEmail(newUser.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (newUser.password !== newUser.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    // Show loading state
    const addButton = document.querySelector('.modal-footer button[type="button"]:last-child');
    if (addButton) {
      addButton.disabled = true;
      addButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Adding...';
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/app-users", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(newUser)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchUsers();
        setShowAddModal(false);
        setNewUser({ firstName: '', lastName: '', email: '', mobile: '', password: '', confirmPassword: '' });
        toast.success("User added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    } finally {
      // Reset loading state
      if (addButton) {
        addButton.disabled = false;
        addButton.innerHTML = 'Submit';
      }
    }
  };

  // Edit User
  const handleEdit = (user) => {
    setEditUser({ ...user, password: '', confirmPassword: '' });
    setEditPasswordMode(false);
    setShowEditModal(true);
  };
  const handleEditSave = async () => {
    if (!editUser.firstName || !editUser.lastName || !editUser.email) {
      toast.error("All fields are required");
      return;
    }
    if (!isValidEmail(editUser.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (editPasswordMode) {
      if (!editUser.password || !editUser.confirmPassword) {
        toast.error("Password fields are required");
        return;
      }
      if (editUser.password !== editUser.confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
    }

    // Show loading state
    const saveButton = document.querySelector('.modal-footer button[type="button"]:last-child');
    if (saveButton) {
      saveButton.disabled = true;
      saveButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Saving...';
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const payload = { ...editUser };
      if (!editPasswordMode) {
        delete payload.password;
        delete payload.confirmPassword;
      }
      const res = await fetch(baseUrl + `/api/app-users/${editUser._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchUsers();
        setShowEditModal(false);
        setEditPasswordMode(false);
        toast.success("User updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    } finally {
      // Reset loading state
      if (saveButton) {
        saveButton.disabled = false;
        saveButton.innerHTML = 'Save';
      }
    }
  };

  // Delete User
  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!deleteId) return;

    // Show loading state
    const deleteButton = document.querySelector('.modal-footer button.btn-danger');
    if (deleteButton) {
      deleteButton.disabled = true;
      deleteButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Deleting...';
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/app-users/${deleteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchUsers();
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      // Reset loading state
      if (deleteButton) {
        deleteButton.disabled = false;
        deleteButton.innerHTML = 'Delete';
      }
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const filtered = users.filter(u => {
    const fields = [u.firstName, u.lastName, u.email, u.mobile].map(val => (val || '').toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().reverse().slice((page-1)*pageSize, page*pageSize);
  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB");
  };
  
  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>App Users</h2>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fa fa-search" style={{
                position: 'absolute',
                left: '12px',
                color: '#8b1634',
                fontSize: '14px'
              }}></i>
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{
                  padding: '10px 12px 10px 36px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  width: '250px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b1634'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
            <button
              onClick={()=>setShowAddModal(true)}
              style={{
                background: '#8b1634',
                color: '#fff',
                fontWeight: '600',
                padding: '10px 20px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6d1128'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#8b1634'}
            >
              <i className="fa fa-plus"></i> Add
            </button>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr minmax(230px, 1.6fr) minmax(120px, 0.9fr) minmax(160px, 1fr) minmax(160px, 1fr) 110px',
            background: '#f7fafc',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>First Name</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Last Name</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Email</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Mobile</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Created Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px', textAlign: 'center'}}>Actions</div>
          </div>

          {/* Table Body */}
          <div>
            {loading ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #8b1634',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '20px'
                }}></div>
                <div style={{
                  color: '#8b1634',
                  fontSize: '16px',
                  fontWeight: '600'
                }}>Loading users...</div>
              </div>
            ) : error ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  fontSize: '48px',
                  color: '#e53e3e',
                  marginBottom: '16px'
                }}>⚠️</div>
                <div style={{
                  color: '#e53e3e',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>Error Loading Data</div>
                <div style={{
                  color: '#718096',
                  fontSize: '14px',
                  textAlign: 'center',
                  maxWidth: '400px',
                  marginBottom: '20px'
                }}>{error}</div>
                <button
                  onClick={fetchUsers}
                  style={{
                    background: '#8b1634',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6d1128'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#8b1634'}
                >
                  <i className="fa fa-refresh"></i>
                  Try Again
                </button>
              </div>
            ) : paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  fontSize: '48px',
                  color: '#cbd5e0',
                  marginBottom: '16px'
                }}>👥</div>
                <div style={{
                  color: '#718096',
                  fontSize: '16px',
                  fontWeight: '600'
                }}>No users found</div>
                {search && (
                  <div style={{
                    color: '#a0aec0',
                    fontSize: '14px',
                    marginTop: '8px'
                  }}>
                    Try adjusting your search criteria
                  </div>
                )}
              </div>
            ) : paged.map((u, idx) => (
              <div 
                key={u._id || idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr minmax(230px, 1.6fr) minmax(120px, 0.9fr) minmax(160px, 1fr) minmax(160px, 1fr) 110px',
                  padding: '16px',
                  borderBottom: '1px solid #f1f5f9',
                  gap: '16px',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{color: '#2d3748', fontSize: '14px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}>{u.firstName}</div>
                <div style={{color: '#2d3748', fontSize: '14px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}>{u.lastName}</div>
                <div style={{color: '#2d3748', fontSize: '14px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{u.email}</div>
                <div style={{color: '#2d3748', fontSize: '14px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}>{u.mobile || '-'}</div>
                <div style={{color: '#2d3748', fontSize: '14px', minWidth: 0}}>{formatDateTime(u.createdAt)}</div>
                <div style={{color: '#2d3748', fontSize: '14px', minWidth: 0}}>{formatDateTime(u.updatedAt)}</div>
                <div style={{display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center'}}>
                  <button 
                    onClick={()=>handleEdit(u)}
                    title="Edit"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '6px',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                  </button>
                  <button 
                    onClick={()=>handleDelete(u._id)}
                    title="Delete"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '6px',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={appUserModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>
                  <i className="fa fa-user-plus" style={{ marginRight: 8, fontSize: 16, opacity: 0.9 }}></i>
                  Add App User
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setShowAddModal(false)}></button>
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>First Name</label>
                    <div className="input-group">
                      <span className="input-group-text" style={appUserInputIconStyle}><i className="fa fa-user"></i></span>
                      <input type="text" className="form-control" style={appUserFieldStyle} autoComplete="off" value={newUser.firstName} onChange={e=>setNewUser({...newUser, firstName: e.target.value})} placeholder="First Name" />
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Last Name</label>
                    <div className="input-group">
                      <span className="input-group-text" style={appUserInputIconStyle}><i className="fa fa-user"></i></span>
                      <input type="text" className="form-control" style={appUserFieldStyle} autoComplete="off" value={newUser.lastName} onChange={e=>setNewUser({...newUser, lastName: e.target.value})} placeholder="Last Name" />
                    </div>
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Email</label>
                    <div className="input-group">
                      <span className="input-group-text" style={appUserInputIconStyle}><i className="fa fa-envelope"></i></span>
                      <input type="email" className="form-control" style={appUserFieldStyle} autoComplete="off" value={newUser.email} onChange={e=>setNewUser({...newUser, email: e.target.value})} placeholder="Email" />
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Mobile</label>
                    <div className="input-group">
                      <span className="input-group-text" style={appUserInputIconStyle}><i className="fa fa-phone"></i></span>
                      <input
                        type="text"
                        className="form-control"
                        style={appUserFieldStyle}
                        autoComplete="off"
                        value={newUser.mobile}
                        onChange={e=>{
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 12);
                          setNewUser({...newUser, mobile: value});
                        }}
                        placeholder="Mobile"
                        maxLength={12}
                        inputMode="numeric"
                        pattern="[0-9]*"
                      />
                    </div>
                  </div>
                </div>
                <div className="row g-3 mt-1" style={appUserPasswordSectionStyle}>
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Password</label>
                    <div className="input-group">
                      <input type={showAddPassword ? "text" : "password"} className="form-control" style={appUserFieldStyle} autoComplete="new-password" value={newUser.password} onChange={e=>setNewUser({...newUser, password: e.target.value})} placeholder="Password" />
                      <span className="input-group-text" style={appUserPasswordToggleStyle} onClick={()=>setShowAddPassword(v=>!v)}>
                        <i className={showAddPassword ? "fa fa-eye-slash" : "fa fa-eye"}></i>
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Confirm Password</label>
                    <div className="input-group">
                      <input type={showAddConfirmPassword ? "text" : "password"} className="form-control" style={appUserFieldStyle} autoComplete="new-password" value={newUser.confirmPassword} onChange={e=>setNewUser({...newUser, confirmPassword: e.target.value})} placeholder="Confirm Password" />
                      <span className="input-group-text" style={appUserPasswordToggleStyle} onClick={()=>setShowAddConfirmPassword(v=>!v)}>
                        <i className={showAddConfirmPassword ? "fa fa-eye-slash" : "fa fa-eye"}></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-between align-items-center" style={adminEditModalFooterStyle}>
                <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={()=>setShowAddModal(false)}>Cancel</button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleAdd}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editUser && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={appUserModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>
                  <i className="fa fa-edit" style={{ marginRight: 8, fontSize: 16, opacity: 0.9 }}></i>
                  Edit App User
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setShowEditModal(false)}></button>
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>First Name</label>
                    <div className="input-group">
                      <span className="input-group-text" style={appUserInputIconStyle}><i className="fa fa-user"></i></span>
                      <input type="text" className="form-control" style={appUserFieldStyle} autoComplete="off" value={editUser.firstName} onChange={e=>setEditUser({...editUser, firstName: e.target.value})} placeholder="First Name" />
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Last Name</label>
                    <div className="input-group">
                      <span className="input-group-text" style={appUserInputIconStyle}><i className="fa fa-user"></i></span>
                      <input type="text" className="form-control" style={appUserFieldStyle} autoComplete="off" value={editUser.lastName} onChange={e=>setEditUser({...editUser, lastName: e.target.value})} placeholder="Last Name" />
                    </div>
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Email</label>
                    <div className="input-group">
                      <span className="input-group-text" style={appUserInputIconStyle}><i className="fa fa-envelope"></i></span>
                      <input type="email" className="form-control" style={{ ...appUserFieldStyle, backgroundColor: "#f3f4f6", color: "#6b7280" }} autoComplete="off" value={editUser.email} onChange={e=>setEditUser({...editUser, email: e.target.value})} placeholder="Email" disabled />
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Mobile</label>
                    <div className="input-group">
                      <span className="input-group-text" style={appUserInputIconStyle}><i className="fa fa-phone"></i></span>
                      <input
                        type="text"
                        className="form-control"
                        style={appUserFieldStyle}
                        autoComplete="off"
                        value={editUser.mobile}
                        onChange={e=>{
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 12);
                          setEditUser({...editUser, mobile: value});
                        }}
                        placeholder="Mobile"
                        maxLength={12}
                        inputMode="numeric"
                        pattern="[0-9]*"
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="mb-3"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    background: "#fdf8f9",
                    borderRadius: 8,
                    border: "1px solid #f0e0e4",
                    marginTop: 12,
                  }}
                >
                  <label className="custom-checkbox" style={{ marginBottom: 0, display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={editPasswordMode}
                      onChange={e => setEditPasswordMode(e.target.checked)}
                    />
                    <span className="checkmark" />
                  </label>
                  <span style={{ color: ADMIN_THEME, fontWeight: 600, fontSize: "0.95rem" }}>
                    <i className="fa fa-lock" style={{ marginRight: 6, fontSize: 13 }}></i>
                    Edit Password
                  </span>
                </div>
                {editPasswordMode && (
                  <div className="row g-3" style={appUserPasswordSectionStyle}>
                    <div className="col-12 col-md-6">
                      <label style={adminEditLabelStyle}>Password</label>
                      <div className="input-group">
                        <input type={showEditPassword ? "text" : "password"} className="form-control" style={appUserFieldStyle} autoComplete="new-password" value={editUser.password || ''} onChange={e=>setEditUser({...editUser, password: e.target.value})} placeholder="Password" />
                        <span className="input-group-text" style={appUserPasswordToggleStyle} onClick={()=>setShowEditPassword(v=>!v)}>
                          <i className={showEditPassword ? "fa fa-eye-slash" : "fa fa-eye"}></i>
                        </span>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <label style={adminEditLabelStyle}>Confirm Password</label>
                      <div className="input-group">
                        <input type={showEditConfirmPassword ? "text" : "password"} className="form-control" style={appUserFieldStyle} autoComplete="new-password" value={editUser.confirmPassword || ''} onChange={e=>setEditUser({...editUser, confirmPassword: e.target.value})} placeholder="Confirm Password" />
                        <span className="input-group-text" style={appUserPasswordToggleStyle} onClick={()=>setShowEditConfirmPassword(v=>!v)}>
                          <i className={showEditConfirmPassword ? "fa fa-eye-slash" : "fa fa-eye"}></i>
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer d-flex justify-content-between align-items-center" style={adminEditModalFooterStyle}>
                <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={()=>setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleEditSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Delete App User</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this user?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
        {/* Pagination */}
        {!loading && !error && filtered.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '18px',
            gap: '12px'
          }}>
            <button
              disabled={page===1}
              onClick={()=>setPage(page-1)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: page===1 ? '#f7fafc' : '#ffffff',
                color: page===1 ? '#a0aec0' : '#8b1634',
                cursor: page===1 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >Prev</button>
            <span style={{
              fontWeight: '700',
              color: '#8b1634',
              fontSize: '16px'
            }}>{page} / {totalPages}</span>
            <button
              disabled={page===totalPages}
              onClick={()=>setPage(page+1)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: page===totalPages ? '#f7fafc' : '#ffffff',
                color: page===totalPages ? '#a0aec0' : '#8b1634',
                cursor: page===totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >Next</button>
          </div>
        )}
      </div>
    </>
  );
}

// Custom checkbox CSS and animations
const style = document.createElement('style');
style.innerHTML = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.fa-spin {
  animation: spin 1s linear infinite;
}
.custom-checkbox input {
  display: none;
}
.custom-checkbox .checkmark {
  display: inline-block;
  width: 18px;
  height: 18px;
  background-color: white;
  border: 2px solid #8b1634;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
}
.custom-checkbox input:checked + .checkmark {
  background-color: #8b1634;
}
.custom-checkbox input:checked + .checkmark::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
`;
if (typeof window !== 'undefined' && !document.getElementById('custom-checkbox-style')) {
  style.id = 'custom-checkbox-style';
  document.head.appendChild(style);
}

export default AppUsersPage;
